import { useEffect, useState } from "react";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { getQueue, processQueue } from "@/utils/offlineQueue";
import axios from "axios";
import { Wifi, WifiOff, RefreshCw, CheckCircle } from "lucide-react";

/**
 * Global offline indicator banner + auto-sync when connection returns.
 * Mount once inside <App /> so it persists across pages.
 */
export function OfflineIndicator() {
  const { isOnline, wasOffline } = useOnlineStatus();
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [queueCount, setQueueCount] = useState(0);

  useEffect(() => {
    setQueueCount(getQueue().length);
  }, [isOnline]);

  // Auto-sync when coming back online
  useEffect(() => {
    if (isOnline && wasOffline) {
      const queue = getQueue();
      if (queue.length > 0) {
        handleSync();
      }
    }
  }, [isOnline]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    const result = await processQueue(axios);
    setSyncing(false);
    setSyncResult(result);
    setQueueCount(getQueue().length);
    // Clear success message after 4 seconds
    if (result.synced > 0) {
      setTimeout(() => setSyncResult(null), 4000);
    }
  };

  // Online + nothing to sync + no recent sync → don't show anything
  if (isOnline && queueCount === 0 && !syncResult) return null;

  return (
    <div className={`fixed top-0 left-0 right-0 z-[100] flex items-center justify-center px-4 py-2.5 text-xs font-medium transition-all ${
      !isOnline
        ? "bg-orange-500 text-white"
        : syncResult?.synced > 0
        ? "bg-green-600 text-white"
        : "bg-primary text-primary-foreground"
    }`}
    data-testid="offline-indicator"
    >
      {!isOnline ? (
        <div className="flex items-center gap-2">
          <WifiOff className="w-3.5 h-3.5 flex-shrink-0" />
          <span>Sem conexão — dados serão sincronizados automaticamente ao reconectar</span>
          {queueCount > 0 && (
            <span className="bg-white/20 px-1.5 py-0.5 rounded-full">
              {queueCount} pendente{queueCount > 1 ? "s" : ""}
            </span>
          )}
        </div>
      ) : syncing ? (
        <div className="flex items-center gap-2">
          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
          <span>Sincronizando dados offline...</span>
        </div>
      ) : syncResult ? (
        <div className="flex items-center gap-2">
          <CheckCircle className="w-3.5 h-3.5" />
          <span>
            {syncResult.synced > 0
              ? `${syncResult.synced} registro(s) sincronizado(s) com sucesso`
              : "Sincronização concluída"}
            {syncResult.failed > 0 && ` · ${syncResult.failed} falhou(aram)`}
          </span>
        </div>
      ) : queueCount > 0 ? (
        <div className="flex items-center gap-2">
          <Wifi className="w-3.5 h-3.5" />
          <span>{queueCount} registro(s) aguardando sincronização</span>
          <button
            onClick={handleSync}
            className="underline hover:no-underline ml-1"
            data-testid="sync-now-btn"
          >
            Sincronizar agora
          </button>
        </div>
      ) : null}
    </div>
  );
}
